import type {
  GuideBeforeAction,
  GuideEdition,
  GuideExperienceDefinition,
  GuideGoal,
  GuideGroup,
  GuideGroupDefinition,
  GuideStep,
  GuideTarget,
  GuideTask,
} from './types'
import { onboardingArchiveEditions } from './archive-editions.generated'

type EditionId = GuideEdition['id']
type ArchiveEdition = (typeof onboardingArchiveEditions)[keyof typeof onboardingArchiveEditions]
type ArchiveTask = ArchiveEdition['tasks'][number]
type ArchiveStep = ArchiveTask['steps'][number]

// 页面实际 path、component、name、title、menuId 均在登录后从后端路由接口动态解析。

const taskIcons: Record<string, string> = {
  'interface-tour': 'i-ph:compass-duotone',
  'project-create': 'i-ph:folder-open-duotone',
  'customer-management': 'i-ph:users-three-duotone',
  'supplier-setup': 'i-ph:buildings-duotone',
  'cooperation-resources': 'i-ph:handshake-duotone',
  'records-quality': 'i-ph:shield-check-duotone',
  'finance-overview': 'i-ph:wallet-duotone',
  'station-overview': 'i-ph:chart-line-up-duotone',
  'station-members': 'i-ph:user-list-duotone',
  'station-settings': 'i-ph:sliders-horizontal-duotone',
  'auxiliary-tools': 'i-ph:toolbox-duotone',
  'organization-permissions': 'i-ph:users-four-duotone',
  'system-config': 'i-ph:gear-six-duotone',
}

const goalIcons: Record<string, string> = {
  project: 'i-ph:folder-open-duotone',
  customer: 'i-ph:users-three-duotone',
  quality: 'i-ph:shield-check-duotone',
  finance: 'i-ph:wallet-duotone',
  admin: 'i-ph:gear-six-duotone',
  station: 'i-ph:broadcast-duotone',
}

function inferTarget(step: ArchiveStep): GuideTarget {
  const value = `${step.id} ${step.target}`.toLowerCase()
  if (/top-nav|header/.test(value)) {
    return 'header'
  }
  if (/side-nav|station-nav|menu/.test(value)) {
    return 'sidebar'
  }
  if (/search/.test(value)) {
    return 'search'
  }
  if (/account|personal/.test(value)) {
    return 'account'
  }
  if (/filter|range|expand/.test(value)) {
    return 'filters'
  }
  if (/table|list|records/.test(value)) {
    return 'table'
  }
  if (/new|create|add|invite|apply/.test(value)) {
    return 'primary'
  }
  if (/tab|nav/.test(value)) {
    return 'tabs'
  }
  if (/kpi|wallet|stat|analysis/.test(value)) {
    return 'stats'
  }
  if (/drawer|form|setting|config|profile|detail|credential|rule/.test(value)) {
    return 'form'
  }
  if (/export|action|risk|safety/.test(value)) {
    return 'toolbar'
  }
  return 'page'
}

function inferSurface(step: ArchiveStep): GuideStep['surface'] {
  const value = `${step.id} ${step.target} ${step.before?.waitFor || ''}`.toLowerCase()
  if (step.before?.click && step.before.safeAction === 'open-surface') {
    return 'detail'
  }
  return /drawer|dialog|detail|panel|modal/.test(value) ? 'detail' : 'list'
}

const directSelectorOverrides: Record<string, string[]> = {
  'top-nav': [
    'header [data-guide-id="top-nav"]',
    'header .business-top-navigation',
    'header .menu-container',
  ],
  'cooperation-overview': ['[data-guide-id="cooperation-overview-module"]'],
  'finance-log-table': ['[data-guide-id="finance-supplier-log-table"]'],
  'finance-employee-log': ['[data-guide-id="finance-employee-log-page"]'],
  'finance-bill-table': ['[data-guide-id="finance-supplier-bill-table"]'],
  'finance-bill-risk': ['[data-guide-id="finance-supplier-bill-risk-actions"]'],
  'finance-employee-bill': ['[data-guide-id="finance-employee-bill-page"]'],
  'auxiliary-dictionary': ['[data-guide-id="auxiliary-dictionary"]'],
  'auxiliary-ip-query': ['[data-guide-id="auxiliary-ip-query"]'],
  'auxiliary-uid-parse': ['[data-guide-id="auxiliary-uid-parse"]'],
  'auxiliary-link-parse': ['[data-guide-id="auxiliary-link-parse"]'],
  'project-risk-tab': ['[data-guide-id="project-risk-panel"]'],
  'org-department-drawer': ['[data-guide-id="organization-department-form"]'],
  'org-role-dialog': ['[data-guide-id="organization-role-form"]'],
  'org-role-menu': [
    '[data-guide-id="organization-role-menu-panel"] .el-drawer__body',
    '[data-guide-id="organization-role-menu-panel"] .el-dialog__body',
  ],
  'org-role-permissions': [
    '[data-guide-id="organization-role-permission-panel"] .el-drawer__body',
    '[data-guide-id="organization-role-permission-panel"] .el-dialog__body',
  ],
  'org-role-diagnostics': [
    '[data-guide-id="organization-role-permission-panel"] .el-drawer__body',
    '[data-guide-id="organization-role-permission-panel"] .el-dialog__body',
  ],
  'org-employee-table': ['[data-guide-id="organization-employee-table"] .employee-table'],
  'org-employee-account': [
    '[data-guide-id="organization-employee-form"]',
  ],
  // 原教程第一步只圈系统设置顶部页签栏，不能圈整个 el-tabs 内容区。
  'config-tabs': [
    '[data-guide-id="config-tabs"] > .el-tabs__header',
    '[data-guide-id="config-tabs"] .el-tabs__header',
  ],
  'config-advanced-scope': ['[data-guide-id="config-advanced-ir"]'],
  'config-advanced-performance': ['[data-guide-id="config-advanced-speed"]'],
  'cooperation-supplier-market': ['[data-guide-id="cooperation-market-module"]'],
  'cooperation-customer-market': ['[data-guide-id="cooperation-market-module"]'],
  'cooperation-supplier-detail': ['[data-guide-id="cooperation-detail-module"]'],
  'cooperation-customer-detail': ['[data-guide-id="cooperation-detail-module"]'],
}

const sampleSelectorOverrides: Record<string, string[]> = {
  'finance-log-table': ['[data-guide-id="finance-member-log-table"]'],
  'finance-employee-log': ['[data-guide-id="finance-employee-log-page"]'],
  'finance-bill-table': ['[data-guide-id="finance-member-bill-table"]'],
  'finance-bill-risk': [
    '[data-guide-id="finance-member-bill-risk-actions"]',
    '[data-guide-id="finance-member-bill-table"]',
  ],
  'finance-employee-bill': ['[data-guide-id="finance-employee-bill-page"]'],
  'finance-invoice-table': ['[data-guide-id="finance-customer-invoice-table"]'],
  'finance-invoice-risk': [
    '[data-guide-id="finance-customer-invoice-toolbar"]',
  ],
}

function getStepSelectors(step: ArchiveStep, editionId: EditionId) {
  if (editionId === 'sample-library' && sampleSelectorOverrides[step.id]) {
    return sampleSelectorOverrides[step.id]
  }
  return directSelectorOverrides[step.id]
    || [adaptSelector(step.target), adaptSelector(step.mobileTarget)].filter(Boolean) as string[]
}

function adaptSelector(selector: string | undefined) {
  if (!selector) {
    return selector
  }
  return selector
    .replace(
      '[data-coop-detail-layer][aria-hidden="false"] .coop-detail-drawer',
      '[data-coop-detail-layer][aria-hidden="false"].coop-detail-drawer',
    )
    .replaceAll('organization-role-menu-drawer', 'organization-role-menu-panel')
    .replaceAll('organization-role-permission-drawer', 'organization-role-permission-panel')
}

function copyBefore(step: ArchiveStep): GuideBeforeAction | undefined {
  const before = step.before
  if (!before) {
    return undefined
  }
  return {
    ...before,
    click: adaptSelector(before.click),
    waitFor: adaptSelector(before.waitFor),
    tab: adaptSelector(before.tab),
    snapshot: adaptSelector(before.snapshot),
    close: before.close?.map(item => adaptSelector(item) || item),
  }
}

const taskDefaultPageKeys: Record<string, string> = {
  'interface-tour': 'workspace',
  'customer-management': 'customer.list',
  'supplier-setup': 'supplier.list',
  'cooperation-resources': 'cooperation.my',
  'project-create': 'project.list',
  'project-operations': 'project.list',
  'records-quality': 'record.survey',
  'record-extensions': 'record.survey',
  'finance-overview': 'finance.supplier.log',
  'station-overview': 'survey.station.dashboard',
  'station-members': 'survey.station.member',
  'station-settings': 'survey.station.settings',
  'auxiliary-tools': 'auxiliary.dictionary',
  'organization-permissions': 'organization.department',
  'system-config': 'config.system.settings',
}

/**
 * 教程只保存稳定的业务 pageKey，不保存接口路由字段。
 * path、fullPath、component、name、title、menuId 在登录后全部从 routeList 接口记录读取。
 */
function resolveStepPageKey(taskId: string, stepId: string, editionId: EditionId) {
  if (taskId === 'finance-overview') {
    const financeKeys: Record<string, string> = {
      'finance-log-filters': editionId === 'sample-library' ? 'finance.member.log' : 'finance.supplier.log',
      'finance-log-table': editionId === 'sample-library' ? 'finance.member.log' : 'finance.supplier.log',
      'finance-log-safety': editionId === 'sample-library' ? 'finance.member.log' : 'finance.supplier.log',
      'finance-employee-log': 'finance.employee.log',
      'finance-bill-filters': editionId === 'sample-library' ? 'finance.member.bill' : 'finance.supplier.bill',
      'finance-bill-table': editionId === 'sample-library' ? 'finance.member.bill' : 'finance.supplier.bill',
      'finance-bill-new': editionId === 'sample-library' ? 'finance.member.bill' : 'finance.supplier.bill',
      'finance-bill-risk': editionId === 'sample-library' ? 'finance.member.bill' : 'finance.supplier.bill',
      'finance-employee-bill': 'finance.employee.bill',
      'finance-invoice-filters': 'finance.customer.invoice',
      'finance-invoice-table': 'finance.customer.invoice',
      'finance-invoice-risk': 'finance.customer.invoice',
    }
    return financeKeys[stepId]
      || (editionId === 'sample-library' ? 'finance.member.log' : 'finance.supplier.log')
  }

  if (taskId === 'organization-permissions') {
    const organizationKeys: Record<string, string> = {
      'org-department-filters': 'organization.department',
      'org-department-table': 'organization.department',
      'org-department-drawer': 'organization.department',
      'org-department-permission': 'organization.department',
      'org-department-manager': 'organization.department',
      'org-role-table': 'organization.role',
      'org-role-dialog': 'organization.role',
      'org-role-menu': 'organization.role',
      'org-role-permissions': 'organization.role',
      'org-role-diagnostics': 'organization.role',
      'org-employee-filters': 'organization.employee',
      'org-employee-table': 'organization.employee',
      'org-employee-account': 'organization.employee',
      'org-employee-assignment': 'organization.employee',
      'org-commission-table': 'organization.commission',
      'org-commission-employees': 'organization.commission',
      'org-commission-conditions': 'organization.commission',
      'org-commission-preview': 'organization.commission',
    }
    return organizationKeys[stepId] || ''
  }

  const exactStepKeys: Record<string, string> = {
    'cooperation-supplier-market': 'cooperation.cloud.supplier',
    'cooperation-supplier-detail': 'cooperation.cloud.supplier',
    'cooperation-customer-market': 'cooperation.cloud.customer',
    'cooperation-customer-detail': 'cooperation.cloud.customer',
    'supplier-members': 'supplier.member.list',
    'supplier-levels': 'supplier.level',
    'supplier-panel-book': 'supplier.panel.book',
    'project-hybrid-api': 'project.hybrid.api',
    'project-schedule': 'project.schedule',
    'project-settlement': 'project.settlement',
    'project-quality': 'project.quality',
    'record-operation-log': 'record.operation.log',
    'record-termination': 'record.termination',
    'record-callback': 'record.callback',
    'record-login': 'record.login',
    'station-analysis': 'survey.station.analysis',
    'station-analysis-kpis': 'survey.station.analysis',
    'station-analysis-charts': 'survey.station.analysis',
    'station-analysis-table': 'survey.station.analysis',
    'station-group-table': 'survey.station.member.group',
    'station-group-drawer': 'survey.station.member.group',
    'station-level-table': 'survey.station.member.level',
    'station-level-drawer': 'survey.station.member.level',
    'station-schedule-filters': 'survey.station.project.schedule',
    'station-schedule-table': 'survey.station.project.schedule',
    'station-schedule-new': 'survey.station.project.schedule',
    'auxiliary-dictionary': 'auxiliary.dictionary',
    'auxiliary-ip-query': 'auxiliary.ip.query',
    'auxiliary-uid-parse': 'auxiliary.uid.parse',
    'auxiliary-link-parse': 'auxiliary.link.parse',
    'config-screen-table': 'config.screen.library',
    'config-screen-designer': 'config.screen.library',
    'config-screen-workflow': 'config.screen.library',
    'config-integrations-grid': 'config.integrations',
    'config-integration-credentials': 'config.integrations.sms',
    'config-integration-test': 'config.integrations.sms',
    'config-events': 'config.events',
    'config-security-table': 'config.security',
    'config-security-credentials': 'config.security',
    'config-security-rules': 'config.security',
    'config-advanced-scope': 'config.advanced',
    'config-advanced-performance': 'config.advanced',
  }

  if (exactStepKeys[stepId]) {
    return exactStepKeys[stepId]
  }
  if (taskId === 'station-overview' && editionId === 'sample-library') {
    return 'survey.station.analysis'
  }
  return taskDefaultPageKeys[taskId] || 'workspace'
}

const latestSampleFinanceContent: Record<string, { title: string, description: string, readOnly?: boolean }> = {
  'finance-log-table': {
    title: '会员财务日志是变动依据',
    description: '先按项目、会员、类型和时间核对收入、支出及余额变化。日志用于解释账单来源，不应直接凭当前余额判断结算。',
  },
  'finance-employee-log': {
    title: '员工财务日志独立核对',
    description: '员工提成或调整应先在员工财务日志确认来源、对象和时间，再进入员工账单。教程不会新增或修改流水。',
    readOnly: true,
  },
  'finance-bill-table': {
    title: '会员账单汇总可结算明细',
    description: '账单把已确认的会员财务明细按周期汇总。创建前核对币种、金额、状态与异常记录，避免重复结算。',
    readOnly: true,
  },
  'finance-bill-risk': {
    title: '创建、审核与支付必须分权复核',
    description: '新增、批量审核、拒绝、支付和删除都会改变真实财务状态。先核对明细、周期和审批人；教程只讲解，不执行。',
    readOnly: true,
  },
  'finance-employee-bill': {
    title: '员工账单承接已确认的员工流水',
    description: '先核对提成规则与员工财务日志，再生成、审核或支付员工账单；不要把员工账单与外部合作方账单混用。',
    readOnly: true,
  },
  'finance-invoice-table': {
    title: '客户发票建立在已确认结算之上',
    description: '先完成项目结算并核对会员与员工成本，再按客户、币种、金额和合同主体准备发票。',
    readOnly: true,
  },
  'finance-invoice-risk': {
    title: '开票、作废与删除都要二次确认',
    description: '开票、批量处理、作废和删除会影响真实财务与税务记录。操作前核对审批状态和合同主体；教程不会点击。',
    readOnly: true,
  },
}

function createStep(taskId: string, archiveStep: ArchiveStep, editionId: EditionId): GuideStep {
  const pageKey = resolveStepPageKey(taskId, archiveStep.id, editionId)
  const latestFinance = editionId === 'sample-library' && taskId === 'finance-overview'
    ? latestSampleFinanceContent[archiveStep.id]
    : undefined

  return {
    id: archiveStep.id,
    pageKey,
    routeMode: pageKey === 'workspace' ? 'workspace' : undefined,
    target: inferTarget(archiveStep),
    type: archiveStep.id,
    surface: inferSurface(archiveStep),
    title: latestFinance?.title || archiveStep.title,
    description: latestFinance?.description || archiveStep.text,
    selectors: getStepSelectors(archiveStep, editionId),
    mobileTarget: adaptSelector(archiveStep.mobileTarget) || undefined,
    interaction: archiveStep.interaction || undefined,
    readOnly: latestFinance?.readOnly ?? archiveStep.readOnly,
    risk: archiveStep.risk || undefined,
    mobileNote: archiveStep.mobileNote || undefined,
    before: copyBefore(archiveStep),
  }
}

function createTask(archiveTask: ArchiveTask, editionId: EditionId): GuideTask {
  return {
    id: archiveTask.id,
    group: archiveTask.group as GuideGroup,
    moduleKey: `onboarding.modules.${archiveTask.id}`,
    titleKey: `onboarding.tasks.${archiveTask.id}.title`,
    descriptionKey: `onboarding.tasks.${archiveTask.id}.description`,
    title: archiveTask.title,
    description: archiveTask.description,
    icon: taskIcons[archiveTask.id] || 'i-ph:book-open-duotone',
    estimate: archiveTask.estimate,
    permission: archiveTask.permission || '',
    permissionFallback: archiveTask.permissionFallback || '',
    goals: [...archiveTask.goals],
    steps: archiveTask.steps.map(step => createStep(archiveTask.id, step, editionId)),
  }
}

const editionNames: Record<string, { id: EditionId, key: string }> = {
  1: { id: 'sample-library', key: 'onboarding.editions.sampleLibrary' },
  2: { id: 'supplier-network', key: 'onboarding.editions.supplierNetwork' },
  3: { id: 'enterprise', key: 'onboarding.editions.enterprise' },
}

// 对齐样本库运营版 v3.74.9 最新压缩包实际运行时的任务路径。
const latestSampleTaskOrder = [
  'interface-tour',
  'customer-management',
  'station-members',
  'project-create',
  'cooperation-resources',
  'station-overview',
  'finance-overview',
  'auxiliary-tools',
  'organization-permissions',
  'system-config',
]

const latestSampleGoalTaskMap: GuideEdition['goalTaskMap'] = {
  project: ['interface-tour', 'customer-management', 'station-members', 'project-create'],
  customer: ['interface-tour', 'customer-management', 'cooperation-resources'],
  quality: ['customer-management', 'station-members', 'project-create', 'station-overview'],
  finance: ['interface-tour', 'customer-management', 'station-members', 'finance-overview'],
  admin: ['organization-permissions', 'system-config'],
  station: ['interface-tour', 'customer-management', 'station-members', 'project-create', 'station-overview'],
}

function createEdition(versionId: '1' | '2' | '3'): GuideEdition {
  const archive = onboardingArchiveEditions[versionId]
  const editionInfo = editionNames[versionId]
  const isLatestSample = editionInfo.id === 'sample-library'
  const taskOrder = isLatestSample ? latestSampleTaskOrder : [...archive.taskOrder]
  const taskRank = new Map(taskOrder.map((taskId, index) => [taskId, index]))
  const tasks = archive.tasks
    .map(task => createTask(task, editionInfo.id))
    .sort((left, right) => (
      (taskRank.get(left.id) ?? Number.MAX_SAFE_INTEGER)
      - (taskRank.get(right.id) ?? Number.MAX_SAFE_INTEGER)
    ))

  return {
    id: editionInfo.id,
    versionId,
    nameKey: editionInfo.key,
    guideVersion: editionInfo.id === 'enterprise'
      ? 'enterprise-1.6.0'
      : isLatestSample ? 'sample-library-2.3.0' : archive.guideVersion,
    groups: archive.groups.map(group => ({ ...group, id: group.id as GuideGroup })) as GuideGroupDefinition[],
    experiences: archive.experienceOptions.map(option => ({ ...option })) as GuideExperienceDefinition[],
    tasks,
    taskOrder,
    goals: archive.goalOptions.map(goal => ({
      ...goal,
      icon: goalIcons[goal.id] || 'i-ph:target-duotone',
    })) as GuideGoal[],
    goalTaskMap: isLatestSample
      ? latestSampleGoalTaskMap
      : Object.fromEntries(
          Object.entries(archive.goalTaskMap).map(([goalId, taskIds]) => [goalId, [...taskIds]]),
        ),
    experienceStepIds: Object.fromEntries(
      Object.entries(archive.experienceStepIds).map(([experience, taskSteps]) => [
        experience,
        Object.fromEntries(Object.entries(taskSteps).map(([taskId, stepIds]) => [taskId, [...stepIds]])),
      ]),
    ) as GuideEdition['experienceStepIds'],
  }
}

export const editions: Record<string, GuideEdition> = {
  1: createEdition('1'),
  2: createEdition('2'),
  3: createEdition('3'),
}

export function getEdition(versionId: string | number | null | undefined): GuideEdition {
  return editions[String(versionId ?? '').trim()] || editions['3']
}
